import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Award, Ban, Calendar, Clock, Copy, Download, FileText, Loader2, ShieldCheck, Users } from 'lucide-react';
import {
  downloadPdf,
  foutmelding,
  genereerPdf,
  haalStatus,
  haalUitgiftes,
  PaspoortUitgifte,
  trekIn,
} from '../services/kwaliteitspaspoortApi';

const jaarPeriode = (jaar: number) => ({ van: `${jaar}-01-01`, tot: `${jaar}-12-31` });
const fmtGetal = (n: number) => n.toLocaleString('nl-NL', { maximumFractionDigits: 1 });
const fmtDag = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });

const Tegel: React.FC<{ icon: React.ReactNode; waarde: string; label: string; bron: string; children?: React.ReactNode }> = ({
  icon,
  waarde,
  label,
  bron,
  children,
}) => (
  <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4">
    <div className="flex items-center gap-2 text-[#0B1F3A]">
      {icon}
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
    </div>
    <p className="mt-2 text-2xl font-bold text-[#0B1F3A]">{waarde}</p>
    <p className="mt-1 text-xs text-gray-500">{bron}</p>
    {children}
  </div>
);

export const KwaliteitspaspoortBlok: React.FC = () => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const huidigJaar = new Date().getFullYear();
  const [periode, setPeriode] = useState(jaarPeriode(huidigJaar));
  const [fout, setFout] = useState<string | null>(null);
  const [gekopieerd, setGekopieerd] = useState<string | null>(null);

  const geldig = /^\d{4}-\d{2}-\d{2}$/.test(periode.van) && /^\d{4}-\d{2}-\d{2}$/.test(periode.tot) && periode.van <= periode.tot;

  const status = useQuery({
    queryKey: ['kwaliteitspaspoort-status', periode.van, periode.tot],
    queryFn: () => haalStatus(periode.van, periode.tot),
    enabled: geldig,
  });

  const uitgiftes = useQuery({ queryKey: ['kwaliteitspaspoort-uitgiftes'], queryFn: haalUitgiftes });

  const genereer = useMutation({
    mutationFn: () => genereerPdf(periode.van, periode.tot),
    onMutate: () => setFout(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kwaliteitspaspoort-uitgiftes'] }),
    onError: async (e) => setFout(await foutmelding(e, t('screening.paspoort.generateError'))),
  });

  const download = useMutation({
    mutationFn: (p: PaspoortUitgifte) => downloadPdf(p),
    onError: async (e) => setFout(await foutmelding(e, t('screening.paspoort.downloadError'))),
  });

  const intrekken = useMutation({
    mutationFn: (id: string) => trekIn(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kwaliteitspaspoort-uitgiftes'] }),
    onError: async (e) => setFout(await foutmelding(e, t('screening.paspoort.revokeError'))),
  });

  const kopieer = async (p: PaspoortUitgifte) => {
    try {
      await navigator.clipboard.writeText(p.verificatie_url);
      setGekopieerd(p.id);
      setTimeout(() => setGekopieerd(null), 2000);
    } catch {
      window.prompt(t('screening.paspoort.copyPrompt'), p.verificatie_url);
    }
  };

  const s = status.data;

  return (
    <section className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0B1F3A10', color: '#0B1F3A' }}>
          <ShieldCheck size={20} />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-800">{t('screening.paspoort.title')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('screening.paspoort.subtitle')}</p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Calendar size={15} className="text-[#0B1F3A]" />
          {t('screening.paspoort.period')}
        </div>
        <label className="text-xs text-gray-500">
          {t('screening.paspoort.from')}
          <input
            type="date"
            value={periode.van}
            max={periode.tot}
            onChange={(e) => setPeriode((p) => ({ ...p, van: e.target.value }))}
            className="mt-1 block rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-800"
          />
        </label>
        <label className="text-xs text-gray-500">
          {t('screening.paspoort.to')}
          <input
            type="date"
            value={periode.tot}
            min={periode.van}
            onChange={(e) => setPeriode((p) => ({ ...p, tot: e.target.value }))}
            className="mt-1 block rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-800"
          />
        </label>
        {[huidigJaar, huidigJaar - 1].map((jaar) => (
          <button
            key={jaar}
            type="button"
            onClick={() => setPeriode(jaarPeriode(jaar))}
            className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-[#0B1F3A] hover:text-[#0B1F3A]"
          >
            {jaar}
          </button>
        ))}
      </div>

      {!geldig && <p className="mt-3 text-xs text-red-600">{t('screening.paspoort.invalidPeriod')}</p>}

      <p className="mt-5 text-sm font-semibold text-gray-700">{t('screening.paspoort.currentStatus')}</p>
      {status.isLoading && geldig ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 rounded-lg bg-gray-100" />
          ))}
        </div>
      ) : status.isError ? (
        <p className="mt-3 text-xs text-red-600">{t('screening.paspoort.statusError')}</p>
      ) : s ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Tegel
            icon={<Award size={15} />}
            label={t('screening.paspoort.peLabel')}
            waarde={t('screening.paspoort.points', { n: fmtGetal(s.pe.punten) })}
            bron={t('screening.paspoort.peSource', { count: s.pe.posten.length })}
          >
            {s.pe.per_register.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
                {s.pe.per_register.map((r) => (
                  <li key={r.register}>
                    {r.register}: <span className="font-semibold">{fmtGetal(r.punten)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Tegel>
          <Tegel
            icon={<Users size={15} />}
            label={t('screening.paspoort.intervisionLabel')}
            waarde={t('screening.paspoort.hours', { n: fmtGetal(s.intervisie.uren) })}
            bron={t('screening.paspoort.intervisionSource', { count: s.intervisie.sessies, teams: s.intervisie.sessies_teams })}
          />
          <Tegel
            icon={<Clock size={15} />}
            label={t('screening.paspoort.practiceLabel')}
            waarde={t('screening.paspoort.hours', { n: fmtGetal(s.praktijk.uren) })}
            bron={t('screening.paspoort.practiceSource', { count: s.praktijk.facturen, clients: s.praktijk.opdrachtgevers })}
          />
        </div>
      ) : null}

      {s?.registercontrole && (s.registercontrole.kvk.status === 'uitgeschreven' || s.registercontrole.verzekering.status === 'verlopen') && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
          <span>
            {[
              s.registercontrole.kvk.status === 'uitgeschreven' && t('screening.paspoort.warnKvk'),
              s.registercontrole.verzekering.status === 'verlopen' && t('screening.paspoort.warnInsurance'),
            ].filter(Boolean).join(' ')}{' '}
            {t('screening.paspoort.warnOnPassport')}
          </span>
        </div>
      )}

      <div className="mt-5 rounded-lg border border-dashed border-[#0B1F3A]/40 p-4">
        <p className="text-sm font-semibold text-gray-700">{t('screening.paspoort.actionTitle')}</p>
        <button
          type="button"
          disabled={!geldig || genereer.isPending}
          onClick={() => genereer.mutate()}
          className="mt-2 inline-flex items-center gap-2 bg-[#0B1F3A] text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {genereer.isPending ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
          {genereer.isPending ? t('screening.paspoort.generating') : t('screening.paspoort.generate')}
        </button>
        <p className="mt-2 text-xs text-gray-500">{t('screening.paspoort.generateHint')}</p>
        {fout && <p className="mt-2 text-xs text-red-600">{fout}</p>}
      </div>

      {uitgiftes.data && uitgiftes.data.length > 0 && (
        <div className="mt-5">
          <p className="text-sm font-semibold text-gray-700">{t('screening.paspoort.issued')}</p>
          <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-100">
            {uitgiftes.data.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-xs">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-800">
                    {fmtDag(p.periode_start)} – {fmtDag(p.periode_eind)}
                    {p.ingetrokken && (
                      <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">
                        {t('screening.paspoort.revoked')}
                      </span>
                    )}
                  </p>
                  <p className="text-gray-500">
                    {t('screening.paspoort.issuedLine', {
                      date: fmtDag(p.created_at),
                      pe: fmtGetal(p.pe_punten),
                      intervision: fmtGetal(p.intervisie_uren),
                      practice: fmtGetal(p.praktijk_uren),
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => download.mutate(p)}
                  className="inline-flex items-center gap-1 font-semibold text-[#0B1F3A] hover:underline"
                >
                  <Download size={13} /> PDF
                </button>
                {!p.ingetrokken && (
                  <>
                    <button type="button" onClick={() => kopieer(p)} className="inline-flex items-center gap-1 font-semibold text-[#0B1F3A] hover:underline">
                      <Copy size={13} /> {gekopieerd === p.id ? t('screening.paspoort.copied') : t('screening.paspoort.copyLink')}
                    </button>
                    <button
                      type="button"
                      disabled={intrekken.isPending}
                      onClick={() => window.confirm(t('screening.paspoort.revokeConfirm')) && intrekken.mutate(p.id)}
                      className="inline-flex items-center gap-1 font-semibold text-red-600 hover:underline disabled:opacity-50"
                    >
                      <Ban size={13} /> {t('screening.paspoort.revoke')}
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
